import {getId, getType} from 'apis/model/base'
import classNames from 'classnames'
import _ from 'lodash'
import React, {useContext, useMemo} from 'react'
import {BsCheckCircle, BsCheckCircleFill} from 'react-icons/bs'
import {TiArrowSortedDown, TiArrowSortedUp} from 'react-icons/ti'
import {useAskCommentAction, useExtraVote} from 'views/Server/ServerContainer'
import {NullAsDefault} from 'views/Shared'
import FeedContext from '../FeedContext'
import {VoteTypes} from '../Vote'
import {renderIfElse} from "../../../views/Shared";
import Translate from "../../../modules/local/Translate";
import TooltipApp from "components/TooltipApp";
import AskContentMeta from "./AskContentMeta";

const renderAction =
    ({onClick = NullAsDefault, canVote = true, isSelected = false, disabled = false}) =>
        (IconComponent = TiArrowSortedUp) => {
            return (
                <IconComponent
                    {...{
                        size: 30,
                        onClick: canVote ? onClick : null,
                        className: classNames(
                            isSelected
                                ? 'text-primary hover:text-gray-700'
                                : 'text-gray-500 hover:text-primary',
                            canVote ? 'cursor-pointer' : 'cursor-not-allow'
                        ),
                    }}
                />
            )
        }

const VoteUpAction = ({
                          owner_id,
                          owner_type,
                          canVote = true,
                          vote_status = false,
                          disabled = false,
                      }) => {
    const handleClick = useExtraVote({
        owner_id,
        owner_type,
        vote_status,
    })
    const isSelected = vote_status === VoteTypes.UP
    return renderAction({
        canVote,
        isSelected,
        onClick: () => {
            if (!disabled) {
                handleClick(isSelected ? VoteTypes.NONE : VoteTypes.UP)
            }
        },
    })(TiArrowSortedUp)
}

const VoteDownAction = ({
                            owner_id,
                            owner_type,
                            canVote = true,
                            vote_status = false,
                            disabled = false,
                        }) => {
    const handleClick = useExtraVote({
        owner_id,
        owner_type,
        vote_status,
    })
    const isSelected = vote_status === VoteTypes.DOWN
    return renderAction({
        canVote,
        isSelected,
        onClick: () => {
            if (!disabled) {
                handleClick(isSelected ? VoteTypes.NONE : VoteTypes.DOWN)
            }
        },
    })(TiArrowSortedDown)
}

export const useCommentHandler = (params = {}) => {
    const {type, id, comment_id} = params
    const {item} = useContext(FeedContext)
    const best_comment = _.get(item, 'best_comment', {})
    const isBestComment = _.isEqual(getId(best_comment), comment_id)
    const [setBestComment, unSetBestComment] = useAskCommentAction(params)

    const canEdit = !!_.get(item, 'edit')

    const child = useMemo(
        () => (
            <Translate>
                {(t) => (
                    <div style={{padding: 3}} className="flex flex-center">
                        {renderIfElse(
                            isBestComment,
                            <TooltipApp title={t('uncheck the best answer')} placement="right"
                                        classNames={{root: "text-xs italic"}}>
                                <BsCheckCircleFill
                                    size={16}
                                    onClick={() => {
                                        unSetBestComment(params)
                                    }}
                                    className="text-green-600 hover:text-green-700 cursor-pointer"
                                />
                            </TooltipApp>,
                            <TooltipApp title={t('choose the best answer')} placement="right"
                                        classNames={{root: "text-xs italic"}}>
                                <BsCheckCircle
                                    size={16}
                                    onClick={() => {
                                        setBestComment(params)
                                    }}
                                    className="text-color-500 hover:text-green-600 cursor-pointer"
                                />
                            </TooltipApp>
                        )}
                    </div>
                )}
            </Translate>
        ),
        [params, isBestComment, setBestComment, unSetBestComment]
    )

    return [
        child,
        {
            isBestComment,
            invisible: _.isEmpty(id) || !canEdit,
        },
    ]
}

export const VoteExtraAction = ({
                                    item,
                                    children,
                                    vote_status,
                                    total_votes,
                                    total_downvotes,
                                    canVote = true,
                                    style,
                                    disabled
                                }) => {
    const owner_id = getId(item)
    const owner_type = getType(item)

    const actionProps = {
        canVote,
        owner_id,
        owner_type,
        vote_status,
        disabled
    }

    return (
        <div
            style={style}
            className="flex flex-col items-center VoteExtraAction">
            <VoteUpAction {...actionProps} />
            <span
                style={{userSelect: 'none'}}
                className="text-xs font-bold text-primary">
                {total_votes}
            </span>
            <VoteDownAction {...actionProps} />
            {children}
        </div>
    )
}

export const AskWrapper = ({item, children, className}) => {
    const {item: feedContentItem} = useContext(FeedContext)

    const isDeleted = Boolean(item?.deleted || item?.isDeleted)

    const [bestCommentAction, {invisible = false, isBestComment = false}] =
        useCommentHandler({
            type: getType(feedContentItem),
            id: getId(feedContentItem),
            comment_id: getId(item),
        })
    const isAnswer = getType(item) === 'comment' && _.get(item, 'container_type') !== 'comment'
    const total_votes = _.get(item, 'total_votes', 0)
    const total_downvotes = _.get(item, 'total_downvotes', 0)

    return (
        !isDeleted &&
        <div className={classNames('AskWrapper flex gap-2 custom-item-render relative', className)}>
            <VoteExtraAction
                // style={{marginLeft: '0.25rem'}}
                item={item}
                canVote={!!!isDeleted}
                disabled={getType(item) !== 'comment'}
                vote_status={_.get(item, 'vote_status')}
                total_votes={total_votes || 0}
                total_downvotes={total_downvotes || 0}>
                {!!!invisible && !!isAnswer && bestCommentAction}
            </VoteExtraAction>
            <div className="flex flex-col flex-1 min-w-0">
                {children}
            </div>
        </div>
    )
}

export const AskContentWrapper = ({children, className}) => {
    const {item} = useContext(FeedContext)

    return <AskWrapper {...{children, className, item}} />
}

export default AskContentWrapper
